import {
  TableContainer,
  TablePagination,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Chip
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getPermissionUsers } from '../../../services/permissions/api'
import { memo, useMemo } from 'react'
import usePermissionsStore from '../../../stores/permissions'
import clsx from 'clsx'
import useCreateUserDialog from '../../../hooks/useEditUserDialog'

const statusToColor: Map<string, 'success'| 'error'> = new Map([
  ['active', 'success'],
  ['disabled', 'error']
])

const Pagination = memo(function TableUsersPagination({ total }: { total: number }) {
  const page = usePermissionsStore(state => state.searchOptions.page)
  const perPage = usePermissionsStore(state => state.searchOptions.perPage)
  const updateSearchOptions = usePermissionsStore(state => state.updateSearchOptions)

  return (
    <TablePagination
      component="div"
      rowsPerPageOptions={[5, 10, 25, 50, 75, 100, 150, 200]}
      count={total}
      rowsPerPage={perPage}
      page={page}
      SelectProps={{
        inputProps: {
          'aria-label': 'rows per page'
        }
      }}
      onPageChange={(e, newPage) => updateSearchOptions({ page: newPage })}
      onRowsPerPageChange={e => updateSearchOptions({ perPage: parseInt(e.target.value, 10) })}
    />
  )
})

const UsersTable = () => {
  const { page, perPage, status, search } = usePermissionsStore(state => state.searchOptions)
  const { data: dataPermissionUsers } = useQuery({
    queryKey: ['permissionUsers'],
    queryFn: () => getPermissionUsers()
  })
  const openEditUserDialog = useCreateUserDialog()

  const users = dataPermissionUsers?.users ?? []

  const filteredUsers = useMemo(() => {
    const newUsers = status !== '' ? users.filter(user => user.status === status) : users

    if (search.length < 0) return newUsers

    return newUsers.filter(user => `${user.first_name} ${user.last_name}`.toLowerCase().includes(search.toLowerCase())
         || user.email.toLowerCase().includes(search.toLowerCase()))
  }, [dataPermissionUsers, search, status])

  const pageUsers = useMemo(() => filteredUsers.slice(page * perPage, page * perPage + perPage), [filteredUsers, page, perPage])

  return (
    <div className="table-wrapper">
      <TableContainer component="div">
        <Table className="users-table" size="medium">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageUsers.map(user => (
              <TableRow
                onClick={() => openEditUserDialog({
                  user
                })}
                key={user.user_id}>
                <TableCell>{user.first_name} {user.last_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.roles.length > 0 ? (
                    <Stack direction="row" spacing={1}>
                      {user.roles.map(role => <Chip
                        className={clsx('role-chip', {
                          'role-chip__admin': role.role_name === 'Admin',
                          'role-chip__dispatcher': role.role_name === 'Dispatcher'
                        })}
                        key={role.role_id} label={role.role_name} />)}
                    </Stack>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip label={user.status} color={statusToColor.get(user.status)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination total={filteredUsers.length} />
    </div>
  )
}
export default UsersTable
